import { useState, useCallback } from 'react'
import { cloneDeep, forEach, forIn, merge, get } from 'lodash'

import { isElementVisible } from 'utils/helpers/domNode'
import {
  FormField,
  FormFieldsData,
  FormMeta,
  GetInitialFormDataParams,
  UseFormParams,
  UseFormReturn,
  ValidateFieldsParams,
  ValidateFormMetaParams,
} from 'types/form'

export const validateFields = ({ formData, fieldName }: ValidateFieldsParams): void => {
  forEach(formData, field => {
    if (field.validator && field.meta.isTouched) {
      field.error = field.validator(formData, field, fieldName)
    }
  })
}

export const validateFormMeta = ({ formData, formMeta }: ValidateFormMetaParams): void => {
  formMeta.errors = []

  forEach(formData, field => {
    if (field.isRequired) {
      const error = field.validator?.(formData, field)

      error && formMeta.errors.push(error)
    }
  })

  formMeta.isValid = !formMeta.errors.length
}

export const getInitialFormData = ({ formConfig, initialFormValues }: GetInitialFormDataParams): FormFieldsData => {
  const config = cloneDeep(formConfig)
  const defaultFieldProps: Partial<FormField> = {
    id: '',
    error: '',
    isRequired: false,
    value: '',
    meta: {
      isTouched: false,
    },
  }

  forIn(config, (value, key) => {
    let field = merge({}, defaultFieldProps, config[key]) as FormField

    if (!field.id) {
      field.id = key
    }

    if (initialFormValues && initialFormValues[key]) {
      field.value = initialFormValues[key]
    }

    config[key] = field
  })

  return config as FormFieldsData
}

export const useForm = ({
  formConfig,
  initialFormValues,
  dataChangeCallback = () => {},
}: UseFormParams): UseFormReturn => {
  const [formData, setFormData] = useState<FormFieldsData>(() => getInitialFormData({ formConfig, initialFormValues }))
  const [formMeta, setFormMeta] = useState<FormMeta>({
    isValid: false,
    errors: [],
  })

  const handleFieldChange = useCallback(
    (name: string) => (value: any) => {
      const updatedFormData = cloneDeep(formData)
      const updatedFormMeta = cloneDeep(formMeta)
      const field = updatedFormData[name]

      field.value = value

      if (!field.meta.isTouched) {
        field.meta.isTouched = true
      }

      validateFields({ formData: updatedFormData, fieldName: name })
      validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta })

      setFormData(updatedFormData)
      setFormMeta(updatedFormMeta)
      dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })
    },
    [formData, formMeta, dataChangeCallback],
  )

  const getFormData = useCallback(
    (fieldsKeys: string[] = []): { [key: string]: any } => {
      const dataToReturn: { [key: string]: any } = {}
      const iterateKeys = fieldsKeys.length ? fieldsKeys : Object.keys(formData)

      iterateKeys.forEach(key => (dataToReturn[key] = formData[key].value || null))

      return dataToReturn
    },
    [formData],
  )

  const setFormElementRef = useCallback(
    (name: string) => (ref: HTMLElement | null) => {
      const fieldRef = get(formData, `${name}.meta.ref`)

      if (!fieldRef && ref) {
        const updatedFormData = cloneDeep(formData)

        updatedFormData[name].meta.ref = ref
        setFormData(updatedFormData)
      }
    },
    [formData],
  )

  const handleFormSubmit = useCallback(
    (response: any) => {
      const updatedFormData = cloneDeep(formData)
      const fieldsWithErrorsRefs: HTMLElement[] = []

      forEach(updatedFormData, field => {
        if (field.responseValidator && field.responseErrorCodes) {
          field.error = field.responseValidator(field.responseErrorCodes, response)

          if (field.meta.ref) {
            fieldsWithErrorsRefs.push(field.meta.ref)
          }
        }
      })

      if (fieldsWithErrorsRefs.length) {
        const fieldRef = fieldsWithErrorsRefs[0]

        if (!isElementVisible(fieldRef)) {
          const fieldRect = fieldRef.getBoundingClientRect()
          const fieldTop = fieldRect?.top + window?.scrollY
          window.scrollTo(0, fieldTop - 150)
        }
      }

      setFormData(updatedFormData)
    },
    [formData],
  )

  const validateForm = useCallback((): void => {
    validateFields({ formData, fieldName: '' })
    validateFormMeta({ formData, formMeta })
  }, [formData, formMeta])

  return {
    formData,
    formMeta,
    handleFieldChange,
    getFormData,
    setFormElementRef,
    handleFormSubmit,
    validateForm,
  }
}
